import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useParticipants,
  useLocalParticipant,
  useTracks,
  VideoTrack,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import { useLocation } from 'react-router-dom';
import { MdMic, MdMicOff, MdCallEnd, MdVolumeUp, MdVolumeOff, MdPerson, MdVideocam, MdVideocamOff, MdPersonAdd, MdFlipCameraAndroid } from 'react-icons/md';
import { hooks } from '../hooks';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';

const MicIcon = MdMic as any;
const MicOffIcon = MdMicOff as any;
const CallEndIcon = MdCallEnd as any;
const VolumeUpIcon = MdVolumeUp as any;
const VolumeOffIcon = MdVolumeOff as any;
const PersonIcon = MdPerson as any;
const PersonAddIcon = MdPersonAdd as any;
const VideocamIcon = MdVideocam as any;
const VideocamOffIcon = MdVideocamOff as any;
const FlipCameraIcon = MdFlipCameraAndroid as any;

interface CallData {
  token: string;
  room_name: string;
  livekit_url: string;
  callee_name?: string;
  caller_name?: string;
  callee_id?: number;
}

// ── Audio helpers ──────────────────────────────────────────────────────────────

function getOrCreateAudioCtx(ref: React.MutableRefObject<AudioContext | null>): AudioContext {
  if (!ref.current || ref.current.state === 'closed') {
    ref.current = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (ref.current.state === 'suspended') ref.current.resume().catch(() => {});
  return ref.current;
}

function playBeep(ctx: AudioContext, startTime: number, freq: number, duration: number, vol = 0.2) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(vol, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + duration + 0.01);
}

function playRingBurst(ctx: AudioContext) {
  const t = ctx.currentTime;
  // Two bursts: 440+480Hz (classic phone ring), burst 1 at t, burst 2 at t+0.55
  playBeep(ctx, t, 440, 0.4);
  playBeep(ctx, t, 480, 0.4);
  playBeep(ctx, t + 0.55, 440, 0.4);
  playBeep(ctx, t + 0.55, 480, 0.4);
}

function playEndTone(ctx: AudioContext) {
  // Three descending tones
  [480, 440, 400].forEach((freq, i) => {
    playBeep(ctx, ctx.currentTime + i * 0.15, freq, 0.12, 0.2);
  });
}

function playRejectedTone(ctx: AudioContext) {
  // Busy signal: 3 short beeps
  [0, 0.45, 0.9].forEach(delay => {
    playBeep(ctx, ctx.currentTime + delay, 480, 0.3, 0.2);
  });
}

// ── CallRoomUI ─────────────────────────────────────────────────────────────────

interface UserItem { id: number; name: string; email: string; }

const CallRoomUI: React.FC<{
  otherName: string;
  roomName: string;
  currentUserId?: number;
  onLeave: () => void;
  rejectedMsg?: string;
  remoteEverJoinedRef: React.MutableRefObject<boolean>;
  audioCtxRef: React.MutableRefObject<AudioContext | null>;
}> = ({ otherName, roomName, currentUserId, onLeave, rejectedMsg, remoteEverJoinedRef, audioCtxRef }) => {
  const { t } = useLanguage();
  const tc = (t as any).incomingCall;
  const participants = useParticipants();
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();
  const videoTracks = useTracks([Track.Source.Camera]);
  const remoteVideoTracks = videoTracks.filter(t =>
    !(t.participant as any).isLocal &&
    (t as any).publication?.isSubscribed &&
    !(t as any).publication?.isMuted
  );
  const hasRemoteVideo = remoteVideoTracks.length > 0;
  const localVideoTrack = videoTracks.find(t => (t.participant as any).isLocal && isCameraEnabled);
  const remoteParticipants = participants.filter((p: any) => !p.isLocal);
  const remoteJoined = remoteParticipants.length > 0;
  const isGroupCall = remoteParticipants.length > 1;
  const prevRemoteJoined = useRef(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [frozenFrame, setFrozenFrame] = useState<string | null>(null);
  const localPipRef = useRef<HTMLDivElement>(null);
  const prevCameraEnabledRef = useRef(false);
  const [isNearFace, setIsNearFace] = useState(false);
  const ringIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isEndingRef = useRef(false);
  const stopProximityRef = useRef<(() => void) | null>(null);

  // Invite modal state
  const [showInvite, setShowInvite] = useState(false);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteStatus, setInviteStatus] = useState<Record<number, 'sending' | 'sent' | 'error'>>({});

  // IDs already in the call (as strings in LiveKit)
  const callParticipantIds = new Set(participants.map((p: any) => String(p.identity)));

  const openInviteModal = useCallback(async () => {
    setShowInvite(true);
    setSearchQuery('');
    if (users.length === 0) {
      try {
        const res = await axiosInstance.get('/users?limit=200');
        const list: UserItem[] = (res.data.users || res.data.data || res.data || []);
        setUsers(list.filter((u: UserItem) => u.id !== currentUserId));
      } catch { setUsers([]); }
    }
  }, [users.length, currentUserId]);

  const handleInvite = useCallback(async (userId: number) => {
    setInviteStatus(s => ({ ...s, [userId]: 'sending' }));
    try {
      await axiosInstance.post('/calls/invite', { room_name: roomName, target_user_id: userId });
      setInviteStatus(s => ({ ...s, [userId]: 'sent' }));
    } catch {
      setInviteStatus(s => ({ ...s, [userId]: 'error' }));
      setTimeout(() => setInviteStatus(s => { const n = { ...s }; delete n[userId]; return n; }), 2000);
    }
  }, [roomName]);

  const audioRoute = (window as any).Capacitor?.Plugins?.AudioRoute;

  const nativeSetEarpiece = useCallback(async () => {
    if (audioRoute) {
      await audioRoute.setEarpiece().catch(() => {});
    }
  }, [audioRoute]);

  const nativeSetSpeaker = useCallback(async () => {
    if (audioRoute) {
      await audioRoute.setSpeaker().catch(() => {});
    }
  }, [audioRoute]);

  const nativeRestoreAudio = useCallback(async () => {
    if (audioRoute) {
      await audioRoute.restore().catch(() => {});
    }
  }, [audioRoute]);

  // Initialize AudioContext (room join is user-triggered, so autoplay is allowed)
  useEffect(() => {
    getOrCreateAudioCtx(audioCtxRef);
  }, [audioCtxRef]);

  // Track that remote ever joined
  useEffect(() => {
    if (remoteJoined) remoteEverJoinedRef.current = true;
  }, [remoteJoined, remoteEverJoinedRef]);

  // Auto-end when remote leaves after having joined
  useEffect(() => {
    if (prevRemoteJoined.current && !remoteJoined) {
      isEndingRef.current = true;
      setIsNearFace(false);
      stopProximityRef.current?.();
      onLeave();
    }
    prevRemoteJoined.current = remoteJoined;
  }, [remoteJoined, onLeave]);

  // Ring tone while waiting for remote
  useEffect(() => {
    if (remoteJoined || rejectedMsg) {
      if (ringIntervalRef.current) clearInterval(ringIntervalRef.current);
      return;
    }
    const ctx = getOrCreateAudioCtx(audioCtxRef);
    playRingBurst(ctx);
    ringIntervalRef.current = setInterval(() => {
      playRingBurst(getOrCreateAudioCtx(audioCtxRef));
    }, 4000);
    return () => { if (ringIntervalRef.current) clearInterval(ringIntervalRef.current); };
  }, [remoteJoined, rejectedMsg, audioCtxRef]);

  // Default earpiece on mount — plugin will enforce via its own loop
  useEffect(() => {
    nativeSetEarpiece();
    return () => { nativeRestoreAudio(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply earpiece when remote joins (WebRTC audio starts flowing at this point)
  useEffect(() => {
    if (remoteJoined && !isSpeakerOn) {
      nativeSetEarpiece();
    }
  }, [remoteJoined, isSpeakerOn, nativeSetEarpiece]);

  // Auto-loudspeaker when camera turns on; auto-earpiece when camera turns off
  useEffect(() => {
    if (isCameraEnabled && !prevCameraEnabledRef.current) {
      setIsSpeakerOn(true);
      nativeSetSpeaker();
    } else if (!isCameraEnabled && prevCameraEnabledRef.current) {
      setIsSpeakerOn(false);
      nativeSetEarpiece();
    }
    prevCameraEnabledRef.current = isCameraEnabled;
  }, [isCameraEnabled, nativeSetSpeaker, nativeSetEarpiece]);

  // Proximity sensor — Capacitor native plugin (Android), fallback to Web API
  useEffect(() => {
    const cap = (window as any).Capacitor;
    if (cap?.isPluginAvailable?.('ProximitySensor')) {
      const plugin = cap.Plugins?.ProximitySensor;
      plugin?.startListening().catch(() => {});
      const handle = plugin?.addListener?.('proximityChange', (data: { near: boolean }) => {
        if (!isEndingRef.current) setIsNearFace(data.near);
      });
      const stop = () => { handle?.remove?.(); plugin?.stopListening?.().catch(() => {}); };
      stopProximityRef.current = stop;
      return stop;
    } else if ('ProximitySensor' in window) {
      try {
        const sensor = new (window as any).ProximitySensor();
        sensor.addEventListener('reading', () => { if (!isEndingRef.current) setIsNearFace(!!sensor.near); });
        sensor.addEventListener('error', () => {});
        sensor.start();
        const stop = () => { try { sensor.stop(); } catch {} };
        stopProximityRef.current = stop;
        return stop;
      } catch {}
    }
  }, []);

  const toggleMic = useCallback(async () => {
    if (localParticipant) {
      await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
    }
  }, [localParticipant, isMicrophoneEnabled]);

  const toggleCamera = useCallback(async () => {
    if (localParticipant) {
      await localParticipant.setCameraEnabled(!isCameraEnabled);
    }
  }, [localParticipant, isCameraEnabled]);

  const switchCamera = useCallback(async () => {
    if (!localParticipant || !isCameraEnabled) return;

    const next: 'user' | 'environment' = facingMode === 'user' ? 'environment' : 'user';
    const pub = (localParticipant as any).getTrackPublication?.(Track.Source.Camera)
      ?? (localParticipant as any).getTrack?.(Track.Source.Camera);
    const lkTrack = pub?.track as any;

    // Capture last frame from local PIP video element before switching
    let captured: string | null = null;
    try {
      const video = localPipRef.current?.querySelector('video');
      if (video && video.videoWidth > 0) {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        // Mirror the canvas if currently on front camera (matches CSS scaleX(-1))
        const ctx = canvas.getContext('2d')!;
        if (facingMode === 'user') {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        }
        ctx.drawImage(video, 0, 0);
        captured = canvas.toDataURL('image/jpeg', 0.85);
      }
    } catch { /* canvas capture failed — fall back to spinner */ }

    setFrozenFrame(captured);
    setIsSwitchingCamera(true);
    try {
      if (typeof lkTrack?.restartTrack === 'function') {
        await lkTrack.restartTrack({ facingMode: { exact: next } });
      } else {
        await localParticipant.setCameraEnabled(false);
        await localParticipant.setCameraEnabled(true, { facingMode: { exact: next } } as any);
      }
      setFacingMode(next);
      // Let new camera emit frames before revealing VideoTrack (avoids flash of play button)
      await new Promise(r => setTimeout(r, 700));
    } catch (err) {
      console.error('switchCamera failed:', err);
    } finally {
      setIsSwitchingCamera(false);
      setFrozenFrame(null);
    }
  }, [localParticipant, isCameraEnabled, facingMode]);

  const toggleSpeaker = useCallback(async () => {
    const next = !isSpeakerOn;
    setIsSpeakerOn(next); // update UI immediately
    if (next) {
      await nativeSetSpeaker();
    } else {
      await nativeSetEarpiece(); // plugin will enforce via its loop
    }
  }, [isSpeakerOn, nativeSetSpeaker, nativeSetEarpiece]);

  const handleEndCall = useCallback(async () => {
    isEndingRef.current = true;
    setIsNearFace(false);
    stopProximityRef.current?.();
    if (ringIntervalRef.current) clearInterval(ringIntervalRef.current);
    const ctx = audioCtxRef.current;
    if (ctx) {
      playEndTone(ctx);
      await new Promise(r => setTimeout(r, 600));
    }
    await nativeRestoreAudio();
    onLeave();
  }, [onLeave, audioCtxRef, nativeRestoreAudio]);

  return (
    <>
      {/* Proximity sensor overlay: blocks touches when phone is near ear */}
      {isNearFace && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: '#000', zIndex: 9999 }}
          onTouchStart={e => e.preventDefault()}
          onTouchEnd={e => e.preventDefault()}
          onPointerDown={e => e.preventDefault()}
        />
      )}

      {/* Main area: remote video grid or avatar */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {/* Remote video grid — 1 participant: full-screen; 2+: split tiles */}
        {hasRemoteVideo && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'grid',
            gridTemplateColumns: remoteVideoTracks.length >= 3 ? '1fr 1fr' : '1fr',
            gridTemplateRows: remoteVideoTracks.length >= 2 ? '1fr 1fr' : '1fr',
            gap: remoteVideoTracks.length > 1 ? 2 : 0,
          }}>
            {remoteVideoTracks.map((track) => (
              <div key={(track.participant as any).identity} style={{ position: 'relative', overflow: 'hidden' }}>
                <VideoTrack
                  trackRef={track}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                {/* Name label per tile in group mode */}
                {remoteVideoTracks.length > 1 && (
                  <div style={{
                    position: 'absolute', bottom: 6, left: 6,
                    backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 6,
                    padding: '2px 7px',
                  }}>
                    <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                      {(track.participant as any).name || (track.participant as any).identity}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Avatar + status (shown when no remote video) */}
        {!hasRemoteVideo && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 20,
          }}>
            {/* Group call: stacked avatars; 1-on-1: single avatar */}
            {isGroupCall ? (
              <div style={{ display: 'flex', position: 'relative', width: 120, height: 80 }}>
                {remoteParticipants.slice(0, 3).map((p: any, i: number) => (
                  <div key={p.identity} style={{
                    position: 'absolute', left: i * 30,
                    width: 70, height: 70, borderRadius: 35, backgroundColor: '#374151',
                    border: '2px solid #10B981',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 3 - i,
                  }}>
                    <PersonIcon size={32} color="#9CA3AF" />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                width: 100, height: 100, borderRadius: 50, backgroundColor: '#374151',
                border: `3px solid ${remoteJoined ? '#10B981' : '#6B7280'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'border-color 0.4s ease',
              }}>
                <PersonIcon size={52} color="#9CA3AF" />
              </div>
            )}

            <p style={{ color: '#F9FAFB', fontSize: 22, fontWeight: 700, margin: 0, textAlign: 'center' }}>
              {isGroupCall
                ? (tc?.groupCall || 'Group Call')
                : (remoteParticipants[0]?.name || remoteParticipants[0]?.identity || otherName)}
            </p>
            {isGroupCall && (
              <p style={{ color: '#10B981', fontSize: 13, margin: 0 }}>
                {remoteParticipants.length + 1} {tc?.participants || 'participants'}
              </p>
            )}
            <p style={{ color: rejectedMsg ? '#EF4444' : remoteJoined ? '#10B981' : '#9CA3AF', fontSize: 14, margin: 0 }}>
              {rejectedMsg || (remoteJoined ? 'Connected' : 'Calling...')}
            </p>
            {!remoteJoined && !rejectedMsg && (
              <div style={{ display: 'flex', gap: 6 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 8, height: 8, borderRadius: 4, backgroundColor: '#6B7280',
                    animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Name overlay — only in 1-on-1 video mode */}
        {hasRemoteVideo && remoteVideoTracks.length === 1 && (
          <div style={{ position: 'absolute', top: 16, left: 0, right: 0, textAlign: 'center' }}>
            <p style={{ color: '#fff', fontSize: 16, fontWeight: 600, margin: 0, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
              {otherName}
            </p>
          </div>
        )}

        {/* Local camera PIP — top-right corner */}
        {(localVideoTrack || isSwitchingCamera) && (
          <div
            ref={localPipRef}
            style={{
              position: 'absolute', top: 60, right: 12, width: 90, height: 130,
              borderRadius: 10, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.3)',
              background: '#1a1a2e',
            }}
          >
            {isSwitchingCamera ? (
              frozenFrame
                ? <img src={frozenFrame} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 22 }}>⟳</div>
            ) : (
              <VideoTrack
                trackRef={localVideoTrack!}
                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
              />
            )}
          </div>
        )}
      </div>

      {/* Control bar */}
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        gap: 20, padding: '20px 0 48px', backgroundColor: 'rgba(17,24,39,0.9)',
      }}>
        {/* Mic */}
        <button onClick={toggleMic} style={{
          width: 52, height: 52, borderRadius: 26,
          backgroundColor: isMicrophoneEnabled ? '#374151' : '#EF4444',
          border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}>
          {isMicrophoneEnabled ? <MicIcon size={24} color="#fff" /> : <MicOffIcon size={24} color="#fff" />}
        </button>

        {/* Camera */}
        <button onClick={toggleCamera} style={{
          width: 52, height: 52, borderRadius: 26,
          backgroundColor: isCameraEnabled ? '#374151' : '#6B7280',
          border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}>
          {isCameraEnabled ? <VideocamIcon size={24} color="#fff" /> : <VideocamOffIcon size={24} color="#fff" />}
        </button>

        {/* Flip camera — disabled when camera is off */}
        <button
          onClick={switchCamera}
          disabled={!isCameraEnabled}
          style={{
            width: 52, height: 52, borderRadius: 26,
            backgroundColor: '#374151',
            border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: isCameraEnabled ? 'pointer' : 'default',
            opacity: isCameraEnabled ? 1 : 0.35,
            transition: 'opacity 0.2s',
          }}
        >
          <FlipCameraIcon size={24} color="#fff" />
        </button>

        {/* End call */}
        <button onClick={handleEndCall} style={{
          width: 64, height: 64, borderRadius: 32, backgroundColor: '#EF4444',
          border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 4px 20px rgba(239,68,68,0.45)',
        }}>
          <CallEndIcon size={30} color="#fff" />
        </button>

        {/* Speaker */}
        <button onClick={toggleSpeaker} style={{
          width: 52, height: 52, borderRadius: 26,
          backgroundColor: isSpeakerOn ? '#2563EB' : '#374151',
          border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}>
          {isSpeakerOn ? <VolumeUpIcon size={24} color="#fff" /> : <VolumeOffIcon size={24} color="#fff" />}
        </button>

        {/* Add person */}
        <button onClick={openInviteModal} style={{
          width: 52, height: 52, borderRadius: 26,
          backgroundColor: '#374151',
          border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        }}>
          <PersonAddIcon size={24} color="#fff" />
        </button>
      </div>

      {/* ── Invite modal ──────────────────────────────────────────────────────── */}
      {showInvite && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'flex-end', zIndex: 10000,
          }}
          onClick={() => setShowInvite(false)}
        >
          <div
            style={{
              width: '100%', backgroundColor: '#1F2937', borderRadius: '20px 20px 0 0',
              padding: '20px 16px 40px', maxHeight: '70vh', display: 'flex', flexDirection: 'column',
            }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ color: '#F9FAFB', fontSize: 16, fontWeight: 700, margin: '0 0 12px', textAlign: 'center' }}>
              {tc?.inviteToCall || 'Invite to Call'}
            </p>
            <input
              type="text"
              placeholder={tc?.searchUser || 'Search user...'}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #374151',
                backgroundColor: '#111827', color: '#F9FAFB', fontSize: 14,
                outline: 'none', boxSizing: 'border-box', marginBottom: 12,
              }}
            />
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {users
                .filter(u => u.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                .map(u => {
                  const inCall = callParticipantIds.has(String(u.id));
                  const status = inviteStatus[u.id];
                  return (
                    <div key={u.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 4px', borderBottom: '1px solid #374151',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 18, backgroundColor: '#374151',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <PersonIcon size={18} color="#9CA3AF" />
                        </div>
                        <p style={{ color: '#F9FAFB', fontSize: 14, margin: 0 }}>{u.name}</p>
                      </div>
                      {inCall ? (
                        <span style={{ color: '#10B981', fontSize: 12 }}>In call</span>
                      ) : status === 'sent' ? (
                        <span style={{ color: '#10B981', fontSize: 12 }}>{tc?.inviteSent || 'Sent'}</span>
                      ) : status === 'error' ? (
                        <span style={{ color: '#EF4444', fontSize: 12 }}>{tc?.inviteFailed || 'Failed'}</span>
                      ) : (
                        <button
                          onClick={() => handleInvite(u.id)}
                          disabled={status === 'sending'}
                          style={{
                            padding: '6px 14px', borderRadius: 8, border: 'none',
                            backgroundColor: status === 'sending' ? '#6B7280' : '#2563EB',
                            color: '#fff', fontSize: 13, cursor: status === 'sending' ? 'default' : 'pointer',
                          }}
                        >
                          {status === 'sending' ? '...' : (tc?.addPerson || 'Invite')}
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── CallScreen ─────────────────────────────────────────────────────────────────

export const CallScreen: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const tc = (t as any).incomingCall;
  const { userId } = hooks.useAppSelector((s: any) => s.authSlice);
  const [callData, setCallData] = useState<CallData | null>(null);
  const [error, setError] = useState('');
  const [rejectedMsg, setRejectedMsg] = useState('');
  const remoteEverJoinedRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    // 1. Router state (in-app navigation)
    const routerState = location.state as CallData | null;
    if (routerState?.token) { setCallData(routerState); return; }

    // 2. Session storage (IncomingCallOverlay accepted)
    const raw = sessionStorage.getItem('livekit_call');
    if (raw) {
      try { setCallData(JSON.parse(raw)); return; } catch {}
    }

    // 3. URL params — opened from FCM notification tap when app was closed
    const params = new URLSearchParams(location.search);
    const roomFromUrl  = params.get('room_name');
    const callerFromUrl = params.get('caller_name');
    const livekitFromUrl = params.get('livekit_url');
    if (roomFromUrl && livekitFromUrl) {
      // Need to fetch a token — user must be authenticated
      axiosInstance.post('/calls/token', {
        room_name: roomFromUrl,
        participant_name: tc?.you || 'Me',
      }).then(res => {
        const data = {
          token: res.data.token,
          room_name: roomFromUrl,
          livekit_url: livekitFromUrl,
          caller_name: callerFromUrl || 'Unknown',
        };
        sessionStorage.setItem('livekit_call', JSON.stringify(data));
        setCallData(data);
      }).catch(() => {
        setError(tc?.noSession || 'No active call session found.');
      });
      return;
    }

    setError(tc?.noSession || 'No active call session found.');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionJoinedRef = useRef(false);

  const handleConnected = useCallback(() => {
    if (!callData?.room_name || sessionJoinedRef.current) return;
    sessionJoinedRef.current = true;
    axiosInstance.post('/calls/session/join', { room_name: callData.room_name }).catch(() => {});
  }, [callData]);

  const hasDisconnectedRef = useRef(false);
  const handleDisconnected = useCallback(() => {
    if (hasDisconnectedRef.current) return;
    hasDisconnectedRef.current = true;
    sessionStorage.removeItem('livekit_call');
    if (sessionJoinedRef.current && callData?.room_name) {
      axiosInstance.post('/calls/session/leave', { room_name: callData.room_name }).catch(() => {});
    }
    if (!remoteEverJoinedRef.current && callData?.callee_id) {
      axiosInstance.post('/calls/cancel', {
        callee_id: callData.callee_id,
        room_name: callData.room_name,
      }).catch(() => {});
    }
    navigate(-1);
  }, [navigate, callData]);

  // Listen for call rejection from callee
  useEffect(() => {
    const handler = (e: Event) => {
      const data = (e as CustomEvent).detail;
      const declinedLabel = (tc as any)?.callDeclined || 'declined the call';
      setRejectedMsg(data?.rejected_by ? `${data.rejected_by} ${declinedLabel}` : 'Call was declined');
      // Play rejected (busy) tone
      const ctx = audioCtxRef.current;
      if (ctx) playRejectedTone(ctx);
      setTimeout(() => handleDisconnected(), 2500);
    };
    window.addEventListener('socket:call:rejected', handler);
    return () => window.removeEventListener('socket:call:rejected', handler);
  }, [handleDisconnected, tc]);

  const otherName = callData?.callee_name || callData?.caller_name || 'Unknown';

  if (error) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', gap: 16, backgroundColor: '#111827',
      }}>
        <p style={{ color: '#EF4444', fontSize: 14 }}>{error}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none',
            backgroundColor: '#6B7280', color: '#fff', cursor: 'pointer', fontSize: 14,
          }}
        >
          {t.common.goBack}
        </button>
      </div>
    );
  }

  if (!callData) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', backgroundColor: '#111827',
      }}>
        <p style={{ color: '#9CA3AF', fontSize: 14 }}>{tc?.connecting || 'Connecting...'}</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.85); }
          50%       { opacity: 1;   transform: scale(1); }
        }
      `}</style>
      <div style={{
        height: '100dvh', width: '100%', backgroundColor: '#111827',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <LiveKitRoom
          token={callData.token}
          serverUrl={callData.livekit_url}
          audio={true}
          video={false}
          data-lk-theme="default"
          style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#111827' }}
          onConnected={handleConnected}
          onDisconnected={handleDisconnected}
        >
          <RoomAudioRenderer />
          <CallRoomUI
            otherName={otherName}
            roomName={callData.room_name}
            currentUserId={userId ? Number(userId) : undefined}
            onLeave={handleDisconnected}
            rejectedMsg={rejectedMsg}
            remoteEverJoinedRef={remoteEverJoinedRef}
            audioCtxRef={audioCtxRef}
          />
        </LiveKitRoom>
      </div>
    </>
  );
};
